"use client";
import { apiSubscribeNewsletter } from "@/apis/page_optimization";
import React, { useState } from "react";
import { toast, ToastContainer } from "react-toastify";

interface Props {
  companyId: string;
}

const FooterNewsletter = ({ companyId }: Props) => {
  const [email, setEmail]       = useState("");
  const [isLoading, setLoading] = useState(false);
  const [success, setSuccess]   = useState(false);

  const validate = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate(email)) {
      toast.error("Please enter a valid email address.", { position: "bottom-right" });
      return;
    }
    setLoading(true);
    try {
      const res = await apiSubscribeNewsletter(companyId, email);
      if (res.message === "Subscribed successfully") {
        setSuccess(true);
        setEmail("");
        toast.success("You're in! Welcome to the club", { autoClose: 2500 });
      } else {
        toast.info("You're already subscribed.");
      }
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <ToastContainer />

      {success ? (
        /* ── success state ── */
        <div className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl px-5 py-4">
          <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center shrink-0 shadow-lg shadow-emerald-500/30">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <p className="text-white font-black text-sm">You&apos;re subscribed!</p>
            <p className="text-slate-400 text-[11px]">Exclusive deals are on their way.</p>
          </div>
        </div>
      ) : (
        /* ── form ── */
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            {/* mail icon */}
            <svg
              className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none"
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              placeholder="your@email.com"
              className="w-full h-12 bg-white/5 border border-white/10 focus:border-blue-500/60 focus:bg-white/10 text-white placeholder:text-slate-500 text-sm font-medium pl-10 pr-4 rounded-2xl outline-none transition-all duration-300 disabled:opacity-50"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="h-12 px-6 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white font-black text-[11px] uppercase tracking-widest rounded-2xl transition-all duration-300 active:scale-95 shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2 shrink-0 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                Subscribe
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7-7 7" />
                </svg>
              </>
            )}
          </button>
        </form>
      )}

      <p className="mt-2.5 text-[10px] text-slate-600 font-medium">
        No spam, ever. Unsubscribe anytime.
      </p>
    </div>
  );
};

export default FooterNewsletter;
